
import axios from 'axios';
import { emptyLocalStorage } from './global';
import { hasVerifiedSession } from '../utils/authSession';
import { isSubscription402, PLAN_RECHECK_EVENT } from '../utils/planState';
import { FEATURE_RECHECK_EVENT } from '../utils/entitlementsRefresh';

// The viewer's IANA timezone, resolved once at module load (not per request).
//
// This used to send `timeZoneName: 'long'` — a human DISPLAY NAME ("Eastern
// European Standard Time", "Gulf Standard Time") rather than a zone id. PHP's
// DateTimeZone cannot parse those, so the backend threw and silently fell back
// to UTC for every country except Saudi Arabia (whose display name happened to
// be the one entry in a stale Windows->IANA lookup table) and Jordan (whose
// display name happens to be a parseable "GMT+03:00"). That single coincidence
// was the only reason the platform ever showed correct times.
//
// resolvedOptions().timeZone gives a real IANA id ("Africa/Tripoli"), which the
// backend already accepts. NOTE: as of the wire-format fix the API emits UTC
// ISO-8601 with an explicit offset and no longer consults this header at all —
// it is kept only for older backend paths that still read it (the legacy device
// campaign feed). Nothing new should depend on it.
const englishTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_NOT_SECRET_CODE,
    withCredentials: true,  // send httpOnly access_token cookie on every request
});

axiosInstance.interceptors.request.use((config) => {
    config.headers['Content-Type'] = 'application/json';
    config.headers['Accept'] = 'application/json';
    config.headers['timezone'] = englishTimeZone;
    return config;
});

export default axiosInstance;

let isLoggingOut = false;

axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        const requestUrl = error?.config?.url || '';
        // Requests the auth pages own: a 401/403/422 here is an answer to show
        // inline (wrong password, dead reset link, unverifiable activation link…),
        // never a reason to tear down a session. NB resend's URL has no leading
        // slash, and /users/logout must reject so the .finally() below can run.
        const isAuthFlowRequest =
            /users\/(login|remote\/login|signup|forget-password|reset-password|verify\/email|resend\/email\/verification|accept\/|logout)/.test(requestUrl);

        // 402 = check.feature gate rejected this request (the user's plan
        // doesn't include the requested feature). Do NOT redirect — that
        // hides what's missing. Let the calling page render an inline
        // FeatureLocked panel via react-query's error state.
        if (status === 402) {
            // "Your subscription expired!": the account has no active package. Tell the
            // router to re-read the plan (it then shows the no-package state); the
            // toast layer drops this message, so nothing pops up.
            if (isSubscription402(error)) window.dispatchEvent(new Event(PLAN_RECHECK_EVENT));
            // The package does not include the feature (CheckFeature, error 'feature_disabled'): the cached
            // plan is stale. Ask for one throttled refetch; nothing is shown and the rejection is unchanged.
            else if (error?.response?.data?.error === 'feature_disabled') window.dispatchEvent(new Event(FEATURE_RECHECK_EVENT));
            return Promise.reject(error);
        }

        // Rate limited — suppress the error to avoid crashing the UI.
        // React-query will not retry 4xx errors (configured in QueryClient).
        if (status === 429) {
            console.warn('[Rate Limited] Too many requests. Please wait a moment.');
            return Promise.reject(error);
        }

        if (status === 401 || status === 403) {
            // Allow auth forms (login/signup/reset) to handle their own errors.
            if (isAuthFlowRequest) {
                return Promise.reject(error);
            }

            if (!isLoggingOut && !window.location.pathname.includes('login')) {
                isLoggingOut = true;
                // Only a session that actually existed is "expired". Decide before
                // emptyLocalStorage() wipes the marker.
                const hadSession = hasVerifiedSession();
                // Tell the server to clear the httpOnly cookie
                axiosInstance.get('/users/logout').catch(() => {}).finally(() => {
                    isLoggingOut = false;
                    emptyLocalStorage();
                    window.location.replace(hadSession ? '/login?reason=session_expired' : '/login');
                });
            }

            // Multiple in-flight requests can fail together on session expiry.
            // Swallow the rejection to avoid crashing React during route teardown.
            return new Promise(() => { });
        }

        return Promise.reject(error);
    }
);
